import type { FC, ReactNode } from "react";

export type BadgeVariant =
  | "default"
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "status";
export type BadgeSize = "sm" | "md" | "lg";

export interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  className?: string;
  icon?: ReactNode;
  pulse?: boolean;
}

const variantStyles: Record<BadgeVariant, string> = {
  danger:
    "border-rose-200 bg-rose-50 text-rose-600 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-400",
  default:
    "border-slate-200 bg-slate-100 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300",
  info: "border-slate-300 bg-slate-100 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
  status:
    "border-slate-200 bg-white text-slate-500 dark:border-slate-600 dark:bg-arti-background dark:text-slate-400",
  success:
    "border-emerald-200 bg-emerald-50 text-emerald-600 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400",
  warning:
    "border-amber-200 bg-amber-50 text-amber-600 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400",
};

const sizeStyles: Record<BadgeSize, string> = {
  lg: "px-3 py-1 text-xs",
  md: "px-2 py-0.5 text-[10px]",
  sm: "px-1.5 py-px text-[9px]",
};

/**
 * Badge Component
 *
 * A versatile badge component for displaying status indicators, counts, and labels.
 *
 * @example
 * ```tsx
 * <Badge variant="success">Active</Badge>
 * <Badge variant="warning" size="lg">3 Pending</Badge>
 * <Badge icon={<Icon />} pulse>Alert</Badge>
 * ```
 */
export const Badge: FC<BadgeProps> = ({
  children,
  variant = "default",
  size = "md",
  className = "",
  icon,
  pulse = false,
}) => (
  <span
    className={`inline-flex items-center justify-center gap-1.5 rounded-none border font-mono font-bold uppercase tracking-widest ${variantStyles[variant]} ${sizeStyles[size]} ${pulse ? "animate-pulse" : ""} ${className} `
      .trim()
      .replace(/\s+/g, " ")}
  >
    {icon}
    {children}
  </span>
);
